import { useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

const POLL_INTERVAL_MS = 5000;

const cleanUsers = (users) => (
  Array.isArray(users)
    ? users.filter((name) => typeof name === 'string' && name.trim() && !name.startsWith('__observer__'))
    : []
);

/**
 * Consulta quiénes están conectados a un hub del aula (chat o pizarra) sin unirse
 * a la sesión, para poder avisar que hay actividad en vivo antes de entrar.
 */
const useAulaHubPresence = ({ enabled, hubUrl, aulaId, accessTokenFactory }) => {
  const [users, setUsers] = useState([]);
  const tokenFactoryRef = useRef(accessTokenFactory);
  tokenFactoryRef.current = accessTokenFactory;

  useEffect(() => {
    if (!enabled || !hubUrl || !aulaId) {
      setUsers((prev) => (prev.length === 0 ? prev : []));
      return undefined;
    }

    let stopped = false;
    let intervalId;

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true,
        accessTokenFactory: () => tokenFactoryRef.current?.() || ''
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    const poll = async () => {
      if (stopped || connection.state !== HubConnectionState.Connected) return;
      try {
        const next = cleanUsers(await connection.invoke('GetConnectedUsers', aulaId));
        if (stopped) return;
        setUsers((prev) => (prev.join('\n') === next.join('\n') ? prev : next));
      } catch (_) {
        /* la presencia es informativa: si falla, se reintenta en el próximo ciclo */
      }
    };

    connection
      .start()
      .then(async () => {
        if (stopped) return;
        await poll();
        intervalId = window.setInterval(poll, POLL_INTERVAL_MS);
      })
      .catch(() => {});

    return () => {
      stopped = true;
      if (intervalId) window.clearInterval(intervalId);
      connection.stop().catch(() => {});
    };
  }, [enabled, hubUrl, aulaId]);

  return users;
};

export default useAulaHubPresence;
